import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AdminPage.css';

const AdminPage = () => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        content: '',
        author: '',
        date: new Date().toISOString().split('T')[0]
    });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        // Validate form
        if (!formData.title || !formData.summary || !formData.content || !formData.author) {
            setMessage({ type: 'error', text: 'Please fill in all required fields.' });
            setIsSubmitting(false);
            return;
        }

        try {
            // Replace with your actual backend API endpoint
            const response = await fetch('http://localhost:8080/api/news', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error('Failed to publish news');
            }

            setMessage({ type: 'success', text: 'News article published successfully!' });

            // Reset form
            setFormData({
                title: '',
                summary: '',
                content: '',
                author: '',
                date: new Date().toISOString().split('T')[0]
            });
        } catch (err) {
            setMessage({
                type: 'error',
                text: `Failed to publish news: ${err.message}. Make sure your backend API is running at http://localhost:8080/api/news`
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="admin-page-container">
            <div className="admin-page-content">
                <div className="admin-header">
                    <div>
                        <h1>Publish News Article</h1>
                        <p className="admin-description">
                            Create and publish new articles to the news site.
                        </p>
                    </div>
                    <div className="admin-user-info">
                        Logged in as: <strong>{user?.username}</strong>
                    </div>
                </div>

                {message.text && (
                    <div className={`admin-message ${message.type}`}>
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="admin-form">
                    <div className="form-group">
                        <label className="form-label" htmlFor="title">
                            Title <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            className="form-input"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter article title"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="author">
                            Author <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="author"
                            name="author"
                            className="form-input"
                            value={formData.author}
                            onChange={handleInputChange}
                            placeholder="Enter author name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="date">
                            Publication Date <span className="required">*</span>
                        </label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            className="form-input"
                            value={formData.date}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="summary">
                            Summary <span className="required">*</span>
                        </label>
                        <textarea
                            id="summary"
                            name="summary"
                            className="form-textarea short"
                            value={formData.summary}
                            onChange={handleInputChange}
                            placeholder="Enter a brief summary (shown in news list)"
                            rows="3"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="content">
                            Article Content <span className="required">*</span>
                        </label>
                        <textarea
                            id="content"
                            name="content"
                            className="form-textarea"
                            value={formData.content}
                            onChange={handleInputChange}
                            placeholder="Enter full article content (HTML tags supported)"
                            rows="12"
                            required
                        />
                        <p className="form-help">
                            You can use HTML tags like &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;strong&gt;, etc.
                        </p>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Publishing...' : 'Publish Article'}
                        </button>
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => setFormData({
                                title: '',
                                summary: '',
                                content: '',
                                author: '',
                                date: new Date().toISOString().split('T')[0]
                            })}
                        >
                            Clear Form
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminPage;
